import { useMemo, useState } from "react"
import { Link } from "@tanstack/react-router"
import { formatDistanceToNowStrict } from "date-fns"

import type { ActivationStageKey } from "@productlane/api/src/lib/reporting/activation-stage"
import { cn } from "@productlane/lib/src/browser"
import {
	Avatar,
	Badge,
	Tabs,
	TabsList,
	TabsTrigger,
	Tooltip,
	Virtualizer,
} from "@productlane/ui"

import type { RouterOutputs } from "@/lib/api"
import { ReportingChartFrame, toneColor } from "@/components/reporting/charts"
import { api } from "@/lib/api"
import { formatWeekRangeLabel } from "./week-label"

type Funnel = RouterOutputs["analytics"]["activationFunnel"]

const RANGE_OPTIONS = [
	{ value: "4", label: "4 weeks" },
	{ value: "8", label: "8 weeks" },
	{ value: "12", label: "12 weeks" },
] as const

const WEEK_MS = 7 * 24 * 60 * 60 * 1000

const planLabel = (plan: string) => plan.charAt(0).toUpperCase() + plan.slice(1)

export function ActivationFunnel() {
	const [weeks, setWeeks] = useState("8")
	const startDate = useMemo(
		() => new Date(Date.now() - Number(weeks) * WEEK_MS),
		[weeks],
	)
	const query = api.analytics.activationFunnel.useQuery(
		{ startDate },
		{ keepPreviousData: true },
	)
	const [selected, setSelected] = useState<ActivationStageKey | null>(null)
	const data = query.data

	const selectedStage =
		selected ?? data?.stages.find((stage) => stage.stuck > 0)?.key ?? null

	return (
		<div className="flex flex-col gap-4">
			<ReportingChartFrame
				title="Activation funnel"
				description={`Workspaces that signed up in the last ${weeks} weeks, and how far each has come. A workspace counts at a stage once it has met every stage before it too. Click a stage to see who is stuck there.`}
				isLoading={query.isLoading}
				isEmpty={!query.isLoading && (data?.total ?? 0) === 0}
				emptyMessage={
					query.isError
						? `Couldn't load the funnel: ${query.error.message}`
						: "No signups in this range"
				}
				legend={
					<Tabs value={weeks} onValueChange={setWeeks}>
						<TabsList variant="transparent">
							{RANGE_OPTIONS.map((option) => (
								<TabsTrigger
									key={option.value}
									variant="transparent"
									value={option.value}
								>
									{option.label}
								</TabsTrigger>
							))}
						</TabsList>
					</Tabs>
				}
			>
				{data ? (
					<FunnelBars
						data={data}
						selected={selectedStage}
						onSelect={setSelected}
					/>
				) : null}
			</ReportingChartFrame>

			{data && selectedStage ? (
				<StuckList data={data} stage={selectedStage} />
			) : null}

			{data && data.total > 0 ? <CohortTable data={data} /> : null}
		</div>
	)
}

function FunnelBars({
	data,
	selected,
	onSelect,
}: {
	data: Funnel
	selected: ActivationStageKey | null
	onSelect: (stage: ActivationStageKey) => void
}) {
	const fill = toneColor("primary")
	return (
		<div className="flex flex-col gap-0.5 text-sm">
			{data.billingUnavailable ? (
				<p className="mb-2 text-xs text-tertiary">
					Stripe did not answer, so every subscription counts as a trial and
					Paying reads low.
				</p>
			) : null}
			{data.stages.map((stage, index) => (
				<button
					key={stage.key}
					type="button"
					onClick={() => onSelect(stage.key)}
					className={cn(
						"grid grid-cols-[10.5rem_minmax(0,1fr)_4rem_4.5rem_5.5rem] items-center gap-3 rounded-md px-2 py-1.5 text-left hover:bg-gray-100 dark:hover:bg-gray-800",
						selected === stage.key && "bg-gray-100 dark:bg-gray-800",
					)}
				>
					<span className="truncate text-secondary">{stage.label}</span>
					<Tooltip
						label={`${stage.reached} of ${data.total} in order, ${stage.ofTotal}%. ${stage.reachedAnyOrder} met this stage at all.`}
						shouldWrapChildren
					>
						<span className="block h-5 w-full">
							<span
								className="block h-full rounded-r"
								style={{
									width: `${Math.max(stage.ofTotal, stage.reached > 0 ? 1 : 0)}%`,
									backgroundColor: fill,
								}}
							/>
						</span>
					</Tooltip>
					<span className="text-right tabular-nums text-primary">
						{stage.reached}
					</span>
					<span className="text-right tabular-nums text-tertiary">
						{index === 0 ? "" : `${stage.fromPrevious}%`}
					</span>
					<span className="text-right tabular-nums text-tertiary">
						{stage.stuck > 0 ? `${stage.stuck} stuck` : ""}
					</span>
				</button>
			))}
			<p className="mt-2 px-2 text-xs text-tertiary">
				{data.convertedCount} of {data.total} cleared every stage and pay.
			</p>
		</div>
	)
}

function StuckList({
	data,
	stage,
}: {
	data: Funnel
	stage: ActivationStageKey
}) {
	const rows = data.stuck[stage]
	const stageInfo = data.stages.find((s) => s.key === stage)
	const label = stageInfo?.label ?? stage
	const total = stageInfo?.stuck ?? rows.length
	return (
		<ReportingChartFrame
			title={`Stuck at ${label}`}
			description={
				total > rows.length
					? `The first stage each of these workspaces has not met. Showing the newest ${rows.length} of ${total}.`
					: "The first stage each of these workspaces has not met. Newest signups first."
			}
			badge={
				<Badge size="sm" variant="important">
					{total}
				</Badge>
			}
			isEmpty={rows.length === 0}
			emptyMessage="Nobody is stuck here"
		>
			<Virtualizer
				className="max-h-[420px] pb-2 text-sm"
				estimateSize={40}
				itemsData={rows}
				itemContent={(row) => (
					<Link
						to="/iluvatar/workspaces/$id"
						params={{ id: row.id }}
						className="flex items-center gap-3 rounded-md px-2 py-2 hover:bg-gray-100 dark:hover:bg-gray-800"
					>
						<Avatar
							size="xs"
							name={row.name || row.domain}
							image={row.logoUrl}
						/>
						<span className="min-w-0 flex-1 truncate font-medium">
							{row.name || row.domain}
						</span>
						<span className="w-56 flex-none truncate text-tertiary">
							{row.creatorEmail ?? ""}
						</span>
						<span className="w-20 flex-none">
							<Badge size="sm">
								{row.isTrialing ? "Trial" : planLabel(row.plan)}
							</Badge>
						</span>
						<span className="w-24 flex-none text-right tabular-nums text-tertiary">
							{formatDistanceToNowStrict(new Date(row.createdAt))}
						</span>
					</Link>
				)}
			/>
		</ReportingChartFrame>
	)
}

function CohortTable({ data }: { data: Funnel }) {
	return (
		<ReportingChartFrame
			title="By signup week"
			description={`Share of each week's signups that reached a stage and every stage before it. Weeks from the last 14 days are still settling, since the agent gets ${data.windowDays} days to earn a first resolution.`}
		>
			<div className="overflow-x-auto">
				<table className="w-full min-w-[720px] text-sm">
					<thead>
						<tr className="border-b border-gray-200 text-xs text-tertiary dark:border-gray-700/80">
							<th className="px-2 pb-2 text-left font-normal">Week</th>
							{data.stages.map((stage) => (
								<th
									key={stage.key}
									className="px-2 pb-2 text-right font-normal"
								>
									{stage.label}
								</th>
							))}
						</tr>
					</thead>
					<tbody>
						{data.cohorts.map((cohort) => (
							<tr
								key={cohort.bucket}
								className="border-b border-gray-200 last:border-0 dark:border-gray-700/80"
							>
								<td className="whitespace-nowrap px-2 py-2 text-secondary">
									{formatWeekRangeLabel(cohort.bucket)}
									{cohort.isPartial ? (
										<span className="ml-1.5 text-xs text-tertiary">
											settling
										</span>
									) : null}
								</td>
								{cohort.stages.map((stage) => (
									<td
										key={stage.key}
										className="px-2 py-2 text-right tabular-nums"
									>
										{stage.key === "signed_up" ? (
											<span className="text-primary">{stage.reached}</span>
										) : (
											<Tooltip
												label={`${stage.reached} of ${cohort.workspaces}`}
												shouldWrapChildren
											>
												<span
													className={
														stage.reached > 0 ? "text-primary" : "text-tertiary"
													}
												>
													{stage.rate}%
												</span>
											</Tooltip>
										)}
									</td>
								))}
							</tr>
						))}
					</tbody>
				</table>
			</div>
		</ReportingChartFrame>
	)
}
